import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { Placement } from "@floating-ui/dom";
import { markOptionalItem } from "app/actions/academySetup";
import { trackChecklistItemSkipped } from "app/utils/analytics-helpers";
import useAcademySetupHint from "app/utils/hooks/useAcademySetupHint";
import useActions from "app/utils/hooks/useActions";
import Coachmark from "../Coachmark";
import { AcademySetupItemKey, academySetupItemConfigs } from "../items";

interface SetupHintProps {
  itemKey: AcademySetupItemKey;
  anchorRef: React.RefObject<HTMLElement>;
  /** See Coachmark: widens the pressed-control dismissal past the anchor. */
  dismissRef?: React.RefObject<HTMLElement>;
  placement?: Placement;
  arrowAtAnchorEnd?: boolean;
  arrowAtAnchorCenter?: boolean;
  /**
   * Copy for a follow-on step of the same item, when finishing it spans two
   * pages (academy_page: save the landing page, then flip its switch in
   * Admin Settings). Defaults to the item's own coachmark copy.
   */
  titleKey?: string;
  bodyKey?: string;
  /**
   * Offers "Skip for now" in the popup for an optional item: the same mark
   * and event as the checklist row's skip link, then back to the setup page.
   * Required items ignore it — they have nothing to skip.
   */
  skippable?: boolean;
}

/**
 * Shows the item's coachmark only when the coach arrived via its checklist
 * button (or a page that carried the hint forward). Items with no designed
 * popup copy render nothing.
 */
const SetupHint = ({
  itemKey,
  anchorRef,
  dismissRef,
  placement,
  arrowAtAnchorEnd,
  arrowAtAnchorCenter,
  titleKey,
  bodyKey,
  skippable = false,
}: SetupHintProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { active, dismiss } = useAcademySetupHint(itemKey);
  const actions = useActions({ markOptionalItem });
  const index = academySetupItemConfigs.findIndex(
    (entry) => entry.key === itemKey,
  );
  const item = index === -1 ? undefined : academySetupItemConfigs[index];
  const title = titleKey || item?.hintTitleKey;
  const body = bodyKey || item?.hintBodyKey;

  if (!active || !item || !title || !body) {
    return null;
  }

  const skip = () => {
    // 1-based position across the whole list, as the row's link reports it.
    trackChecklistItemSkipped(item.key, item.phase, index + 1);
    // The CTA that opened this page is already recorded, so the skip's
    // completion attributes to it without a second mark.
    actions.markOptionalItem(item.key, "skipped");
    // Clear the hint from this entry first so Back cannot resurrect it.
    dismiss();
    history.push("/academy/setup");
  };

  return (
    <Coachmark
      anchorRef={anchorRef}
      title={t(title)}
      body={t(body)}
      onDismiss={dismiss}
      onSkip={skippable && item.optional ? skip : undefined}
      dismissRef={dismissRef}
      placement={placement}
      arrowAtAnchorEnd={arrowAtAnchorEnd}
      arrowAtAnchorCenter={arrowAtAnchorCenter}
    />
  );
};

export default SetupHint;
